<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  invoices: {
    type: Array,
    required: true,
  },
  unlinkedTaxInvoices: {
    type: Array,
    default: () => [],
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['openSendModal']);
const { t } = useI18n();

const formatDate = value => {
  if (!value) return '—';

  const date = String(value).slice(0, 10).split('-');
  return date.length === 3 && date[0].length === 4
    ? `${date[2]}/${date[1]}/${date[0]}`
    : value;
};

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const invoiceStatus = status => {
  if (status === 'RECEIVED' || status === 'CONFIRMED') {
    return t('BILLING.INVOICES.PAID');
  }

  if (status === 'PENDING') return t('BILLING.INVOICES.PENDING');
  return t('BILLING.INVOICES.OVERDUE');
};

const invoiceStatusClass = status => {
  if (status === 'RECEIVED' || status === 'CONFIRMED') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  }

  if (status === 'PENDING') {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  }

  return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
};

const taxInvoiceStatus = status => {
  const labels = {
    SCHEDULED: t('BILLING.INVOICES.TAX_SCHEDULED'),
    AUTHORIZED: t('BILLING.INVOICES.TAX_AUTHORIZED'),
    PROCESSING_CANCELLATION: t('BILLING.INVOICES.TAX_PROCESSING_CANCELLATION'),
    CANCELED: t('BILLING.INVOICES.TAX_CANCELED'),
    CANCELLATION_DENIED: t('BILLING.INVOICES.TAX_CANCELLATION_DENIED'),
    ERROR: t('BILLING.INVOICES.TAX_ERROR'),
  };

  return labels[status] || status || t('BILLING.INVOICES.TAX_PROCESSING');
};

const taxInvoiceStatusClass = status => {
  if (status === 'AUTHORIZED') return 'text-emerald-600 dark:text-emerald-400';
  if (status === 'SCHEDULED' || status === 'PROCESSING') {
    return 'text-amber-600 dark:text-amber-400';
  }
  if (
    status === 'ERROR' ||
    status === 'CANCELED' ||
    status === 'CANCELLATION_DENIED'
  ) {
    return 'text-rose-600 dark:text-rose-400';
  }
  return 'text-slate-600 dark:text-slate-300';
};
</script>

<template>
  <div
    class="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
  >
    <div
      class="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3"
    >
      <span class="i-lucide-receipt text-xl text-blue-600 dark:text-blue-400" />
      <div>
        <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {{ $t('BILLING.INVOICES.TITLE') }}
        </h2>
        <p class="text-sm text-slate-500 mt-0.5">
          {{ $t('BILLING.INVOICES.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div v-if="invoices.length === 0" class="text-center py-10">
      <span
        class="i-lucide-receipt text-4xl text-slate-300 dark:text-slate-600 mb-3 block"
      />
      <p class="text-slate-500">{{ $t('BILLING.INVOICES.EMPTY') }}</p>
    </div>

    <div v-else class="overflow-x-auto">
      <table
        class="w-full min-w-[780px] text-left text-sm text-slate-600 dark:text-slate-300"
      >
        <thead
          class="bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 dark:text-slate-400"
        >
          <tr>
            <th class="px-5 py-3 font-medium">
              {{ $t('BILLING.INVOICES.DUE_DATE') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ $t('BILLING.INVOICES.VALUE') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ $t('BILLING.INVOICES.STATUS') }}
            </th>
            <th class="px-4 py-3 font-medium min-w-[260px]">
              {{ $t('BILLING.INVOICES.TAX_TITLE') }}
            </th>
            <th v-if="!readOnly" class="px-5 py-3 font-medium text-right">
              {{ $t('BILLING.INVOICES.ACTION') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
          <tr
            v-for="invoice in invoices"
            :key="invoice.id"
            class="hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors"
          >
            <td v-if="!readOnly" class="px-5 py-4 align-middle">
              <div class="flex items-center gap-2">
                <span class="i-lucide-calendar-days text-blue-500" />
                <span class="font-semibold text-slate-800 dark:text-slate-100">
                  {{ formatDate(invoice.dueDate) }}
                </span>
              </div>
            </td>
            <td
              class="px-4 py-4 align-middle font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap"
            >
              {{ formatCurrency(invoice.value) }}
            </td>
            <td class="px-4 py-4 align-middle">
              <span
                class="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                :class="invoiceStatusClass(invoice.status)"
              >
                {{ invoiceStatus(invoice.status) }}
              </span>
            </td>
            <td class="px-4 py-4 align-middle min-w-[260px]">
              <div v-if="invoice.tax_invoices?.length" class="space-y-1">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="i-lucide-file-check text-emerald-600 dark:text-emerald-400"
                  />
                  <span
                    class="font-medium text-slate-800 dark:text-slate-100 truncate"
                    :title="$t('BILLING.INVOICES.TAX_LINKED_LABEL')"
                  >
                    {{ $t('BILLING.INVOICES.TAX_LINKED_LABEL') }}
                  </span>
                </div>
                <div
                  class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
                >
                  <span
                    class="font-semibold"
                    :class="
                      taxInvoiceStatusClass(invoice.tax_invoices[0].status)
                    "
                  >
                    {{ taxInvoiceStatus(invoice.tax_invoices[0].status) }}
                  </span>
                  <span class="text-slate-400">
                    {{
                      formatDate(
                        invoice.tax_invoices[0].effectiveDate ||
                          invoice.tax_invoices[0].scheduledDate
                      )
                    }}
                  </span>
                  <a
                    v-if="invoice.tax_invoices[0].pdfUrl"
                    :href="invoice.tax_invoices[0].pdfUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium"
                  >
                    {{ $t('BILLING.INVOICES.TAX_PDF') }}
                  </a>
                  <a
                    v-if="invoice.tax_invoices[0].xmlUrl"
                    :href="invoice.tax_invoices[0].xmlUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-slate-600 hover:text-slate-900 dark:text-slate-300 font-medium"
                  >
                    {{ $t('BILLING.INVOICES.TAX_XML') }}
                  </a>
                  <span
                    v-if="
                      !invoice.tax_invoices[0].pdfUrl &&
                      !invoice.tax_invoices[0].xmlUrl
                    "
                    class="text-slate-400"
                  >
                    {{ $t('BILLING.INVOICES.TAX_UNAVAILABLE') }}
                  </span>
                  <span
                    v-if="invoice.tax_invoices.length > 1"
                    class="text-slate-400"
                  >
                    {{ `+${invoice.tax_invoices.length - 1}` }}
                  </span>
                </div>
              </div>
              <div
                v-else
                class="flex items-center gap-2 text-xs text-slate-400"
              >
                <span class="i-lucide-file-clock" />
                <span>{{ $t('BILLING.INVOICES.TAX_PENDING') }}</span>
              </div>
            </td>
            <td class="px-5 py-4 align-middle">
              <div class="flex items-center justify-end gap-3">
                <a
                  v-if="invoice.invoiceUrl && !readOnly"
                  :href="invoice.invoiceUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium"
                >
                  {{ $t('BILLING.INVOICES.PAY') }}
                  <span class="i-lucide-external-link text-xs" />
                </a>
                <button
                  v-if="!readOnly"
                  class="text-slate-400 hover:text-blue-500 transition-colors"
                  :title="$t('BILLING.INVOICES.SEND_EMAIL')"
                  @click="emit('openSendModal', invoice, 'email')"
                >
                  <span class="i-lucide-mail" />
                </button>
                <button
                  v-if="!readOnly"
                  class="text-slate-400 hover:text-emerald-500 transition-colors"
                  :title="$t('BILLING.INVOICES.SEND_WHATSAPP')"
                  @click="emit('openSendModal', invoice, 'whatsapp')"
                >
                  <span class="i-lucide-message-circle" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <details
      v-if="unlinkedTaxInvoices.length"
      class="mx-5 my-3 border-t border-slate-200 dark:border-slate-800 pt-3 text-sm"
    >
      <summary
        class="cursor-pointer list-none flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <span class="i-lucide-triangle-alert text-amber-500" />
        <span>{{ $t('BILLING.INVOICES.TAX_ORPHAN_TITLE') }}</span>
        <span class="text-xs text-slate-400">
          {{ unlinkedTaxInvoices.length }}
        </span>
        <span class="i-lucide-chevron-down ml-auto text-xs" />
      </summary>
      <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {{ $t('BILLING.INVOICES.TAX_ORPHAN_DESCRIPTION') }}
      </p>
      <div class="mt-2 space-y-1">
        <div
          v-for="taxInvoice in unlinkedTaxInvoices"
          :key="taxInvoice.id"
          class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300"
        >
          <span class="font-medium">
            {{
              formatDate(taxInvoice.effectiveDate || taxInvoice.scheduledDate)
            }}
          </span>
          <span>{{ formatCurrency(taxInvoice.value) }}</span>
          <span
            :class="taxInvoiceStatusClass(taxInvoice.status)"
            class="font-semibold"
          >
            {{ taxInvoiceStatus(taxInvoice.status) }}
          </span>
          <a
            v-if="taxInvoice.pdfUrl"
            :href="taxInvoice.pdfUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium"
          >
            {{ $t('BILLING.INVOICES.TAX_PDF') }}
          </a>
          <a
            v-if="taxInvoice.xmlUrl"
            :href="taxInvoice.xmlUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-slate-600 hover:text-slate-900 dark:text-slate-300 font-medium"
          >
            {{ $t('BILLING.INVOICES.TAX_XML') }}
          </a>
          <span
            v-if="!taxInvoice.pdfUrl && !taxInvoice.xmlUrl"
            class="text-slate-400"
          >
            {{ $t('BILLING.INVOICES.TAX_UNAVAILABLE') }}
          </span>
        </div>
      </div>
    </details>
  </div>
</template>
